import { withStyles } from '@material-ui/core';
import { SwitchBaseProps } from '@material-ui/core/internal/SwitchBase';
import React, { useCallback, useMemo } from 'react';
import { Row } from 'react-table';
import { Checkbox } from '../Checkbox';
import { CellProps, HeaderCellProps, TableColumn } from './types';

const GlobalCheckbox = withStyles({
  root: {
    padding: '0!important',
  },
})(Checkbox);

const ItemCheckbox = withStyles({
  root: {
    padding: '0!important',
  },
})(Checkbox);

export const makeSelectColumn = <Data extends Record<string, any>>(
  isCheckBoxShown: (data: Data, index: number) => boolean,
  isDisabled?: (data: Data, index: number) => boolean,
): TableColumn<Data> => ({
  id: 'selection',
  width: 30,
  disableSortBy: true,
  accessor: isCheckBoxShown,
  Header: ({
    rows,
    getToggleAllRowsSelectedProps,
    toggleRowSelected,
    selectedFlatRows,
  }: HeaderCellProps<Data>) => {
    const isRowSelectable = useCallback((row: Row<Data>) => {
      return (
        isCheckBoxShown(row.original, row.index) &&
        (!isDisabled || !isDisabled(row.original, row.index))
      );
    }, []);

    const selectedRowsCount = selectedFlatRows.length;

    const { checked, indeterminate } = useMemo(() => {
      const selectableRows = rows.filter(isRowSelectable).length;
      const checked = selectedRowsCount > 0 && selectedRowsCount === selectableRows;
      const indeterminate = selectedRowsCount > 0 && selectedRowsCount < selectableRows;
      return { checked, indeterminate };
    }, [isRowSelectable, rows, selectedRowsCount]);

    const overriddenOnChange: SwitchBaseProps['onChange'] = (event) => {
      rows.forEach((row) => {
        if (isRowSelectable(row)) {
          toggleRowSelected(row.id, event.currentTarget.checked);
        }
      });
    };

    return (
      <GlobalCheckbox
        {...getToggleAllRowsSelectedProps()}
        onChange={overriddenOnChange}
        checked={checked}
        indeterminate={indeterminate}
        color='primary'
      />
    );
  },
  Cell: ({ row, value }: CellProps<Data, boolean>) => {
    const { onChange, ...props } = row.getToggleRowSelectedProps();
    const canClick = value && (!isDisabled || !isDisabled(row.original, row.index));
    const overriddenOnChange: SwitchBaseProps['onChange'] = useCallback(
      (e) => {
        if (canClick) {
          onChange && onChange(e);
        }
      },
      [onChange, canClick],
    );

    if (!value) {
      return null;
    }
    return (
      <ItemCheckbox
        onChange={overriddenOnChange}
        {...props}
        disabled={isDisabled && isDisabled(row.original, row.index)}
        color='primary'
      />
    );
  },
});
